import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";

const MONTHS = ["January","February","March","April","May","June",
  "July","August","September","October","November","December"];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec"];

const CATEGORIES = [
  { id: "basic",          label: "Basic" },
  { id: "supplier",       label: "Supplier" },
  { id: "subcontractor",  label: "Sub-Contractor" },
  { id: "extra",          label: "Extra" },
];

const STATUS_CYCLE = { due: "overdue", overdue: "covered", covered: "paid", paid: "due" };
const STATUS_CFG = {
  due:     { label: "Due",     bg: "#eff6ff", color: "#1e40af" },
  overdue: { label: "Overdue", bg: "#fee2e2", color: "#dc2626" },
  covered: { label: "Covered", bg: "#f0fdfa", color: "#0d9488" },
  paid:    { label: "Paid",    bg: "#dcfce7", color: "#16a34a" },
};

const ROW_GRID = "1.5fr 2fr 120px 100px 100px 32px 32px";

function fmt(amount) {
  return Number(amount).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

function clampDay(year, month, day) {
  return Math.min(day, new Date(year, month + 1, 0).getDate());
}

const FREQ_CFG = {
  fortnightly: { label: "Fortnightly" },
  monthly:   { step: 1,  count: 12, label: "Monthly" },
  quarterly: { step: 3,  count: 8,  label: "Quarterly" },
  biannual:  { step: 6,  count: 4,  label: "Every 6 months" },
  annual:    { step: 12, count: 3,  label: "Annual" },
};

function generateDates(startDateStr, frequency) {
  if (frequency === "fortnightly") {
    const start = new Date(startDateStr);
    return Array.from({ length: 52 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i * 14);
      return d.toISOString().split("T")[0];
    });
  }
  const start = new Date(startDateStr);
  const startYear = start.getFullYear();
  const startMonth = start.getMonth();
  const day = start.getDate();
  const { step, count } = FREQ_CFG[frequency];
  return Array.from({ length: count }, (_, i) => {
    const totalMonth = startMonth + i * step;
    const y = startYear + Math.floor(totalMonth / 12);
    const m = totalMonth % 12;
    const d = clampDay(y, m, day);
    return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  });
}

// ── Edit Modal ────────────────────────────────────────────────────────────────

function EditBillModal({ bill, allBills, onClose, onSaved }) {
  const [creditor, setCreditor] = useState(bill.creditor);
  const [information, setInformation] = useState(bill.information || "");
  const [dueDate, setDueDate] = useState(bill.due_date || "");
  const [amount, setAmount] = useState(bill.amount);
  const [category, setCategory] = useState(bill.category || "basic");
  const [applyAll, setApplyAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isRecurring = !!bill.recurring_group;

  const handleSave = async () => {
    if (!creditor.trim() || !dueDate || !amount) {
      setError("Creditor, due date and amount are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const newDay = new Date(dueDate).getDate();
      const basePayload = { creditor: creditor.trim(), information, amount: parseFloat(amount), category };

      if (isRecurring && applyAll) {
        const siblings = allBills.filter(b => b.recurring_group === bill.recurring_group);
        const isFortnightly = bill.recurring_frequency === "fortnightly";
        await Promise.all(siblings.map(b => {
          if (isFortnightly) {
            return axios.patch(`${API}/bills/${b.id}/`, { ...basePayload, due_date: b.due_date });
          }
          const d = new Date(b.due_date);
          const clamped = clampDay(d.getFullYear(), d.getMonth(), newDay);
          const newDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,"0")}-${String(clamped).padStart(2,"0")}`;
          return axios.patch(`${API}/bills/${b.id}/`, { ...basePayload, due_date: newDate });
        }));
      } else {
        await axios.patch(`${API}/bills/${bill.id}/`, { ...basePayload, due_date: dueDate });
      }

      onSaved();
      onClose();
    } catch {
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SimpleModal
      title={isRecurring ? "Edit Recurring Bill" : "Edit Bill"}
      onClose={onClose}
      onSave={handleSave}
      saving={saving}
      error={error}
    >
      <div style={fieldStyle}>
        <label style={labelStyle}>Creditor</label>
        <input style={inputStyle} value={creditor} onChange={e => setCreditor(e.target.value)} autoFocus />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Information</label>
        <textarea style={{ ...inputStyle, resize: "vertical", minHeight: "60px" }}
          value={information} onChange={e => setInformation(e.target.value)} />
      </div>
      <div style={{ ...fieldStyle, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <div>
          <label style={labelStyle}>Due Date</label>
          <input type="date" style={inputStyle} value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Amount ($)</label>
          <input type="number" step="0.01" style={inputStyle} value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Category</label>
        <select style={inputStyle} value={category} onChange={e => setCategory(e.target.value)}>
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      {isRecurring && (
        <div style={{ marginTop: "16px", padding: "12px 14px", backgroundColor: "#fffbeb", borderRadius: "8px", border: "1px solid #fde68a" }}>
          {bill.recurring_frequency && (
            <div style={{ fontSize: "12px", color: "#92400e", marginBottom: "8px", fontWeight: 600 }}>
              {FREQ_CFG[bill.recurring_frequency]?.label || "Recurring"} bill
            </div>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#92400e", cursor: "pointer" }}>
            <input type="checkbox" checked={applyAll} onChange={e => setApplyAll(e.target.checked)} />
            Apply changes to all recurring copies
            <span style={{ fontSize: "11px", color: "#b45309" }}>
              {bill.recurring_frequency === "fortnightly" ? "(each copy keeps its own date)" : "(due date adjusts per period)"}
            </span>
          </label>
        </div>
      )}
    </SimpleModal>
  );
}

// ── Bill Row ──────────────────────────────────────────────────────────────────

function BillRow({ bill, allBills, onSaved, viewMonthIdx, viewYear }) {
  const [toggling, setToggling] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);

  const billDate = bill.due_date ? new Date(bill.due_date) : null;
  const isCarried = billDate
    ? billDate.getFullYear() * 12 + billDate.getMonth() < viewYear * 12 + viewMonthIdx
    : false;

  const handleToggleStatus = async () => {
    if (toggling) return;
    setToggling(true);
    try {
      await axios.patch(`${API}/bills/${bill.id}/`, { status: STATUS_CYCLE[bill.status] });
      onSaved();
    } finally { setToggling(false); }
  };

  const handleToggleHighlight = async () => {
    await axios.patch(`${API}/bills/${bill.id}/`, { highlighted: !bill.highlighted });
    onSaved();
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    await axios.delete(`${API}/bills/${bill.id}/`);
    onSaved();
  };

  const paid = bill.status === "paid";
  const cfg = STATUS_CFG[bill.status] || STATUS_CFG.due;

  return (
    <>
      <div
        onClick={handleToggleHighlight}
        style={{
          display: "grid", gridTemplateColumns: ROW_GRID,
          padding: "9px 20px", borderBottom: `1px solid ${colors.border}`,
          alignItems: "center", gap: "10px",
          backgroundColor: bill.highlighted ? "#fefce8" : colors.white,
          opacity: paid ? 0.55 : 1, transition: "background-color 0.15s, opacity 0.15s",
          borderLeft: isCarried ? "3px solid #fde68a" : bill.highlighted ? "3px solid #f59e0b" : "3px solid transparent",
          cursor: "pointer",
        }}
      >
        {/* Creditor */}
        <div style={{ fontWeight: 600, fontSize: "13px", textDecoration: paid ? "line-through" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {bill.creditor}
        </div>

        {/* Information */}
        <div style={{ fontSize: "12px", color: colors.slate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {bill.information || <span style={{ color: "#cbd5e1" }}>—</span>}
        </div>

        {/* Due Date */}
        <div style={{ fontSize: "13px", color: colors.slate, display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
          <span>{bill.due_date ? new Date(bill.due_date).toLocaleDateString("en-AU") : "—"}</span>
          {isCarried && billDate && (
            <span style={{ fontSize: "10px", fontWeight: 700, backgroundColor: "#fef3c7", color: "#d97706", padding: "1px 6px", borderRadius: "8px", whiteSpace: "nowrap" }}>
              Carried from {MONTH_SHORT[billDate.getMonth()]}
            </span>
          )}
        </div>

        {/* Amount */}
        <div style={{ fontSize: "13px", fontWeight: 600 }}>
          {fmt(bill.amount)}
        </div>

        {/* Status toggle */}
        <div>
          <span
            onClick={(e) => { e.stopPropagation(); if (!toggling) handleToggleStatus(); }}
            title="Click to cycle: Due → Overdue → Covered → Paid"
            style={{
              display: "inline-block", fontSize: "11px", fontWeight: 700,
              padding: "3px 10px", borderRadius: "10px",
              backgroundColor: cfg.bg, color: cfg.color,
              cursor: "pointer", userSelect: "none",
              opacity: toggling ? 0.5 : 1, transition: "opacity 0.15s",
            }}
          >{cfg.label}</span>
        </div>

        {/* Edit */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            title="Edit bill"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "4px", borderRadius: "4px" }}
          >
            <LuPencil size={14} />
          </button>
        </div>

        {/* Delete */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(); }}
            title={confirmDelete ? "Click again to confirm" : "Delete"}
            style={{ background: "none", border: "none", cursor: "pointer", color: confirmDelete ? "#dc2626" : "#cbd5e1", padding: "4px", borderRadius: "4px", transition: "color 0.15s" }}
          >
            <LuTrash2 size={14} />
          </button>
        </div>
      </div>

      {editing && (
        <EditBillModal
          bill={bill}
          allBills={allBills}
          onClose={() => setEditing(false)}
          onSaved={onSaved}
        />
      )}
    </>
  );
}

// ── Add Bill Row ──────────────────────────────────────────────────────────────

function AddBillRow({ monthIdx, year, category: defaultCategory, onSaved }) {
  const defaultDate = `${year}-${String(monthIdx + 1).padStart(2, "0")}-01`;
  const [creditor, setCreditor] = useState("");
  const [information, setInformation] = useState("");
  const [dueDate, setDueDate] = useState(defaultDate);
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [frequency, setFrequency] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDueDate(`${year}-${String(monthIdx + 1).padStart(2, "0")}-01`);
    setCategory(defaultCategory);
  }, [monthIdx, year, defaultCategory]);

  const handleSave = async () => {
    if (!creditor.trim() || !dueDate || !amount) return;
    setSaving(true);
    try {
      const group = frequency ? crypto.randomUUID() : "";
      const dates = frequency ? generateDates(dueDate, frequency) : [dueDate];
      await Promise.all(dates.map(dateStr =>
        axios.post(`${API}/bills/`, {
          creditor: creditor.trim(), information, due_date: dateStr,
          amount: parseFloat(amount), status: "due", category,
          recurring_group: group, recurring_frequency: frequency,
        })
      ));

      setCreditor(""); setInformation(""); setAmount(""); setFrequency("");
      setDueDate(defaultDate);
      onSaved();
    } finally { setSaving(false); }
  };

  const selectStyle = { fontSize: "13px", padding: "5px 8px", border: `1px solid ${colors.border}`, borderRadius: "6px", width: "100%", backgroundColor: colors.white };

  return (
    <div style={{ borderTop: `2px dashed ${colors.border}`, backgroundColor: "#f8fafc" }}>
      <div style={{ display: "grid", gridTemplateColumns: ROW_GRID, padding: "10px 20px", gap: "10px", alignItems: "center" }}>
        <input type="text" placeholder="Creditor *" value={creditor}
          onChange={e => setCreditor(e.target.value)}
          style={{ fontSize: "13px", padding: "5px 8px", border: `1px solid ${colors.border}`, borderRadius: "6px" }}
        />
        <input type="text" placeholder="Information" value={information}
          onChange={e => setInformation(e.target.value)}
          style={{ fontSize: "13px", padding: "5px 8px", border: `1px solid ${colors.border}`, borderRadius: "6px" }}
        />
        <input type="date" value={dueDate}
          onChange={e => setDueDate(e.target.value)}
          style={{ fontSize: "13px", padding: "5px 8px", border: `1px solid ${colors.border}`, borderRadius: "6px" }}
        />
        <input type="number" step="0.01" placeholder="Amount *" value={amount}
          onChange={e => setAmount(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
          style={{ fontSize: "13px", padding: "5px 8px", border: `1px solid ${colors.border}`, borderRadius: "6px" }}
        />
        <select value={category} onChange={e => setCategory(e.target.value)} style={selectStyle}>
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button
          onClick={handleSave}
          disabled={saving || !creditor.trim() || !amount}
          style={{
            background: "#1e3a5f", color: "#fff", border: "none",
            borderRadius: "6px", padding: "5px 0", fontSize: "12px",
            fontWeight: 700, cursor: "pointer",
            opacity: (!creditor.trim() || !amount) ? 0.4 : 1,
            gridColumn: "span 2",
          }}
        >{saving ? "…" : "Add"}</button>
      </div>
      <div style={{ padding: "0 20px 10px", display: "flex", alignItems: "center", gap: "8px" }}>
        <label style={{ fontSize: "12px", color: colors.slate, fontWeight: 600 }}>Frequency:</label>
        <select
          value={frequency}
          onChange={e => setFrequency(e.target.value)}
          style={{ fontSize: "12px", padding: "3px 8px", border: `1px solid ${colors.border}`, borderRadius: "6px", backgroundColor: colors.white }}
        >
          <option value="">One-off</option>
          <option value="fortnightly">Fortnightly (every 2 weeks, 2 years)</option>
          <option value="monthly">Monthly (12/year)</option>
          <option value="quarterly">Quarterly (every 3 months, 2 years)</option>
          <option value="biannual">Every 6 months (2 years)</option>
          <option value="annual">Annual (3 years)</option>
        </select>
        {frequency && (
          <span style={{ fontSize: "11px", color: "#0891b2" }}>
            → {generateDates(dueDate || `${year}-${String(monthIdx+1).padStart(2,"0")}-01`, frequency).length} bills will be created
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function BillsPage({ initialCatTab = "basic" }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [monthTab, setMonthTab] = useState(now.getMonth());
  const [catTab, setCatTab] = useState(initialCatTab);
  const [year, setYear] = useState(now.getFullYear());
  const [showPaid, setShowPaid] = useState(false);

  const changeYear = (delta) => {
    setYear(y => y + delta);
    setMonthTab(0);
  };

  const loadBills = useCallback(() => {
    axios.get(`${API}/bills/`)
      .then(res => setBills(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadBills(); }, [loadBills]);

  // All bills in the current month (all categories) — for totals
  const monthBills = bills.filter(b => {
    if (!b.due_date) return false;
    const d = new Date(b.due_date);
    return d.getMonth() === monthTab && d.getFullYear() === year;
  });

  const BASIC_CARRY_FROM_YM = 2026 * 12 + 5; // June 2026
  const viewYM = year * 12 + monthTab;

  // Unpaid/uncovered bills from previous months carried forward (all categories; basic from June 2026+)
  const carriedBills = bills.filter(b => {
    if (!b.due_date || b.status === "paid" || b.category !== catTab) return false;
    const d = new Date(b.due_date);
    const billYM = d.getFullYear() * 12 + d.getMonth();
    if (billYM >= viewYM) return false;
    if (catTab === "basic" && billYM < BASIC_CARRY_FROM_YM) return false;
    return true;
  }).sort((a, b) => a.due_date.localeCompare(b.due_date));

  // Bills in the current month + category — for the list
  const currentCatBills = monthBills
    .filter(b => b.category === catTab)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));

  const visibleBills = [...carriedBills, ...currentCatBills]
    .filter(b => showPaid || b.status !== "paid");

  const total = monthBills.reduce((s, b) => s + parseFloat(b.amount), 0);
  const paidTotal = monthBills.filter(b => b.status === "paid" || b.status === "covered").reduce((s, b) => s + parseFloat(b.amount), 0);
  const outstanding = total - paidTotal;

  const catCarriedUnpaid = (cat) => bills.filter(b => {
    if (!b.due_date || b.status === "paid" || b.category !== cat) return false;
    const d = new Date(b.due_date);
    const billYM = d.getFullYear() * 12 + d.getMonth();
    if (billYM >= viewYM) return false;
    if (cat === "basic" && billYM < BASIC_CARRY_FROM_YM) return false;
    return true;
  });
  const catTotal = (cat) => {
    const monthAmt   = monthBills.filter(b => b.category === cat).reduce((s, b) => s + parseFloat(b.amount || 0), 0);
    const carriedAmt = catCarriedUnpaid(cat).reduce((s, b) => s + parseFloat(b.amount || 0), 0);
    return monthAmt + carriedAmt;
  };
  const catPaid = (cat) => monthBills
    .filter(b => b.category === cat && (b.status === "paid" || b.status === "covered"))
    .reduce((s, b) => s + parseFloat(b.amount || 0), 0);

  const monthTabStyle = (i) => ({
    padding: "9px 12px", fontSize: "13px",
    fontWeight: monthTab === i ? 700 : 500,
    color: monthTab === i ? "#1e3a5f" : colors.slate,
    border: "none",
    borderBottom: monthTab === i ? "2px solid #1e3a5f" : "2px solid transparent",
    background: "none", cursor: "pointer", whiteSpace: "nowrap",
  });

  const catTabStyle = (id) => ({
    padding: "7px 16px", fontSize: "12px",
    fontWeight: catTab === id ? 700 : 500,
    color: catTab === id ? "#1e3a5f" : colors.slate,
    border: "none",
    borderBottom: catTab === id ? "2px solid #1e3a5f" : "2px solid transparent",
    background: "none", cursor: "pointer", whiteSpace: "nowrap",
  });

  const catCount = (id) => {
    const inMonth = monthBills.filter(b => b.category === id).length;
    if (id === "basic") return inMonth;
    const carried = bills.filter(b => {
      if (!b.due_date || b.status === "paid" || b.category !== id) return false;
      const d = new Date(b.due_date);
      return d.getFullYear() * 12 + d.getMonth() < year * 12 + monthTab;
    }).length;
    return inMonth + carried;
  };

  const monthCount = (i) => bills.filter(b => {
    if (!b.due_date) return false;
    const d = new Date(b.due_date);
    return d.getMonth() === i && d.getFullYear() === year;
  }).length;

  if (loading) return <div style={styles.loading}>Loading…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={styles.pageTitle}>Bills Tracker</div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <button onClick={() => changeYear(-1)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: colors.slate, padding: "2px 6px", lineHeight: 1 }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: "15px", minWidth: "44px", textAlign: "center" }}>{year}</span>
            <button onClick={() => changeYear(1)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: colors.slate, padding: "2px 6px", lineHeight: 1 }}>›</button>
          </div>
        </div>
        <div style={{ fontSize: "13px", color: colors.slate }}>
          {bills.length} bills across all months
        </div>
      </div>

      {/* Month tab bar */}
      <div style={{
        display: "flex", overflowX: "auto",
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.white, padding: "0 20px", flexShrink: 0,
      }}>
        {MONTHS.map((m, i) => (
          <button key={i} style={monthTabStyle(i)} onClick={() => setMonthTab(i)}>
            {MONTH_SHORT[i]}
            {monthCount(i) > 0 && (
              <span style={{ marginLeft: "4px", fontSize: "10px", color: i === monthTab ? "#1e3a5f" : colors.slate }}>
                ({monthCount(i)})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Category tab bar */}
      <div style={{
        display: "flex", alignItems: "center",
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: "#f8fafc", padding: "0 20px", flexShrink: 0,
      }}>
        {CATEGORIES.map(c => (
          <button key={c.id} style={catTabStyle(c.id)} onClick={() => setCatTab(c.id)}>
            {c.label}
            {catCount(c.id) > 0 && (
              <span style={{ marginLeft: "4px", fontSize: "10px", color: catTab === c.id ? "#1e3a5f" : colors.slate }}>
                ({catCount(c.id)})
              </span>
            )}
          </button>
        ))}
        <button
          onClick={() => setShowPaid(p => !p)}
          style={{
            marginLeft: "auto", fontSize: "11px", fontWeight: 600,
            padding: "3px 10px", borderRadius: "12px", cursor: "pointer",
            border: `1px solid ${colors.border}`,
            backgroundColor: showPaid ? "#dcfce7" : colors.white,
            color: showPaid ? "#16a34a" : colors.slate,
            whiteSpace: "nowrap",
          }}
        >
          {showPaid
            ? "Hide paid"
            : `Show paid (${monthBills.filter(b => b.category === catTab && b.status === "paid").length})`}
        </button>
      </div>

      {/* Column headers */}
      <div style={{
        display: "grid", gridTemplateColumns: ROW_GRID,
        padding: "6px 20px", backgroundColor: "#f1f5f9",
        borderBottom: `1px solid ${colors.border}`,
        fontSize: "11px", fontWeight: 700, color: colors.slate,
        textTransform: "uppercase", letterSpacing: "0.4px", gap: "10px",
        flexShrink: 0,
      }}>
        <div>Creditor</div>
        <div>Information</div>
        <div>Due Date</div>
        <div>Amount</div>
        <div>Status</div>
        <div />
        <div />
      </div>

      {/* Bill rows */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {visibleBills.length === 0 && (
          <div style={{ padding: "30px 20px", color: colors.slate, fontSize: "14px", textAlign: "center" }}>
            No {CATEGORIES.find(c => c.id === catTab)?.label} bills for {MONTHS[monthTab]}.
          </div>
        )}
        {visibleBills.map(b => (
          <BillRow key={b.id} bill={b} allBills={bills} onSaved={loadBills} viewMonthIdx={monthTab} viewYear={year} />
        ))}

        <AddBillRow monthIdx={monthTab} year={year} category={catTab} onSaved={loadBills} />
      </div>

      {/* Per-category subtotals */}
      <div style={{
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: "#f8fafc",
        padding: "10px 20px",
        display: "flex", gap: "24px", flexWrap: "wrap", flexShrink: 0,
      }}>
        {CATEGORIES.map(c => {
          const tot  = catTotal(c.id);
          const paid = catPaid(c.id);
          const owed = tot - paid;
          return (
            <div key={c.id} style={{ display: "flex", flexDirection: "column", minWidth: "90px" }}>
              <div style={{ fontSize: "9px", fontWeight: 700, color: colors.slate, textTransform: "uppercase", letterSpacing: "0.5px" }}>{c.label}</div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: owed > 0 ? "#dc2626" : "#16a34a" }}>{fmt(owed)}</div>
              <div style={{ fontSize: "10px", color: colors.slate }}>{fmt(tot)} total</div>
            </div>
          );
        })}
      </div>

      {/* Totals footer — whole month, all categories */}
      <div style={{
        borderTop: `2px solid ${colors.border}`,
        backgroundColor: colors.white,
        padding: "12px 20px",
        display: "flex", gap: "32px", alignItems: "center",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, color: colors.slate, textTransform: "uppercase", letterSpacing: "0.5px" }}>Month Total</div>
          <div style={{ fontSize: "20px", fontWeight: 800, color: colors.darkGrey }}>{fmt(total)}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, color: colors.slate, textTransform: "uppercase", letterSpacing: "0.5px" }}>Settled</div>
          <div style={{ fontSize: "20px", fontWeight: 800, color: "#16a34a" }}>{fmt(paidTotal)}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, color: colors.slate, textTransform: "uppercase", letterSpacing: "0.5px" }}>Outstanding</div>
          <div style={{ fontSize: "20px", fontWeight: 800, color: outstanding > 0 ? "#dc2626" : "#16a34a" }}>{fmt(outstanding)}</div>
        </div>
        {monthBills.length > 0 && (
          <div style={{ marginLeft: "auto", fontSize: "12px", color: colors.slate }}>
            {monthBills.filter(b => b.status === "paid").length} of {monthBills.length} paid across all categories
          </div>
        )}
      </div>
    </div>
  );
}
